import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { API_URL } from '../utils/Constants/URLS'
import { X, IndianRupee, ShieldCheck, MapPin, Truck, Plus, Trash2 } from 'lucide-react'
import { Row, Col, FormGroup, Label, Input, Spinner } from 'reactstrap'

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    height: 30,
    fontSize: '11px',
    borderColor: state.selectProps.hasError ? '#fb7185' : base.borderColor,
    backgroundColor: state.selectProps.hasError ? '#fff1f2' : base.backgroundColor,
    boxShadow: 'none'
  }),
  valueContainer: base => ({ ...base, padding: '0 4px', height: 28 }),
  singleValue: base => ({ ...base, margin: 0, fontSize: '11px' }),
  placeholder: base => ({ ...base, margin: 0, fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  input: base => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: base => ({ ...base, height: 28 }),
  dropdownIndicator: base => ({ ...base, padding: '0 4px' }),
  clearIndicator: base => ({ ...base, padding: '0 4px' }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
}

const selectProps = {
  styles: selectStyles,
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed'
}

const CUSTOMER_ADDRESSES = {
  SILIGURI: `GREENERGY SOLUTION PVT. LTD. \nBock 'C' 1st. Floor, PCM Bus Terminus, \nSevoke Road Siliguri, \nDist-Darjeeling, West Bengal-734001`,
  KOLKATA: `GREENERGY SOLUTION PVT. LTD. \nZ-44, 39 Chak Garia, Panchasayar, \nKolkata-700094`,
  GUWAHATI: `GREENERGY SOLUTION PVT. LTD. \nGopal Boro Path, \nBy Lane 2, Gorchuk, \nGuwahati-781035`
}

const detectCustomerPreset = (address) => {
  if (!address) return ''
  const entry = Object.entries(CUSTOMER_ADDRESSES).find(([, val]) => val === address)
  return entry ? entry[0] : ''
}

const toDateInput = (value) => {
  if (!value) return ''
  try {
    return new Date(value).toISOString().split('T')[0]
  } catch {
    return typeof value === 'string' ? value.split('T')[0] : ''
  }
}

const newLineItem = () => ({
  localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  businessLine: '',
  productLine: '',
  productLineMeta: null,
  model: '',
  modelMeta: null,
  quantity: 1,
  perUnitPurchasePrice: '',
  totalPurchasePrice: '',
  gst: 18,
  requiredDateOfMaterial: new Date().toISOString().split('T')[0],
  committedDeliveryDate: new Date().toISOString().split('T')[0]
})

const getLineItemHierarchy = (modelObj) => {
  if (!modelObj || typeof modelObj === 'string') {
    return { businessLine: '', productLine: '', productLineMeta: null }
  }
  const productLine = modelObj.productLine
  const productLineObj = productLine && typeof productLine === 'object' ? productLine : null
  const plId = productLineObj?._id || productLineObj?.id || productLine || ''
  const blId =
    productLineObj?.businessLine?._id ||
    productLineObj?.businessLine?.id ||
    productLineObj?.businessLine ||
    modelObj.businessLine?._id ||
    modelObj.businessLine?.id ||
    modelObj.businessLine ||
    ''
  return {
    businessLine: blId ? String(blId) : '',
    productLine: plId ? String(plId) : '',
    productLineMeta: productLineObj
  }
}

const toModelOption = (model) => ({
  value: String(model._id || model.id),
  label: model.modelName || 'Model',
  modelData: model
})

const toProductLineOption = (productLine) => ({
  value: String(productLine._id || productLine.id),
  label: productLine.productLineName || 'Product Line',
  productLineData: productLine
})

const EditPurchaseOrder = ({ isOpen, onClose, purchaseOrder, onSuccess }) => {
  const token = localStorage.getItem('token')

  const [stores, setStores] = useState([])
  const [vendors, setVendors] = useState([])
  const [quotations, setQuotations] = useState([])
  const [businessLineList, setBusinessLineList] = useState([])
  const [productLinesCache, setProductLinesCache] = useState({})
  const [modelsCache, setModelsCache] = useState({})

  const [selectedVendor, setSelectedVendor] = useState('')
  const [selectedQuotation, setSelectedQuotation] = useState('')
  const [selectedStore, setSelectedStore] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [customerAddressPreset, setCustomerAddressPreset] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')
  const [gsplGstin, setGsplGstin] = useState('')
  const [orderPlacedDate, setOrderPlacedDate] = useState('')
  const [status, setStatus] = useState('open')
  const [lineItems, setLineItems] = useState([])

  const [terms, setTerms] = useState({
    priceValidity: '',
    paymentTerms: '',
    packing: '',
    freight: '',
    transitInsurance: ''
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const productLinesCacheRef = useRef({})
  const modelsCacheRef = useRef({})

  const loadProductLines = async (businessLineId, extraProductLines = []) => {
    if (!businessLineId) return []

    const map = new Map()
    ;(productLinesCacheRef.current[businessLineId] || []).forEach(opt => map.set(opt.value, opt))
    extraProductLines.forEach(pl => {
      if (pl) map.set(String(pl._id || pl.id), toProductLineOption(pl))
    })

    if (map.size > 0 && productLinesCacheRef.current[businessLineId]) {
      return Array.from(map.values())
    }

    const res = await fetch(
      `${API_URL}product-lines?businessLine=${businessLineId}&pagination=false`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )
    const result = await res.json()
    ;(result.data?.docs || []).forEach(product => {
      map.set(String(product.id || product._id), toProductLineOption(product))
    })
    extraProductLines.forEach(pl => {
      if (pl) map.set(String(pl._id || pl.id), toProductLineOption(pl))
    })
    const options = Array.from(map.values())
    productLinesCacheRef.current[businessLineId] = options
    setProductLinesCache({ ...productLinesCacheRef.current })
    return options
  }

  const loadModels = async (businessLineId, productLineId, extraModels = []) => {
    const cacheKey = `${businessLineId}:${productLineId}`
    if (!businessLineId || !productLineId) return []

    if (modelsCacheRef.current[cacheKey]) {
      return modelsCacheRef.current[cacheKey]
    }

    const res = await fetch(
      `${API_URL}models?pagination=false&businessLine=${businessLineId}&productLine=${productLineId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )
    const result = await res.json()
    const docs = result.data?.docs || result.message?.docs || result.docs || []
    const map = new Map()
    docs.forEach(m => map.set(String(m._id || m.id), toModelOption(m)))
    extraModels.forEach(m => {
      const id = String(m._id || m.id)
      if (!map.has(id)) map.set(id, toModelOption(m))
    })
    const options = Array.from(map.values())
    modelsCacheRef.current[cacheKey] = options
    setModelsCache({ ...modelsCacheRef.current })
    return options
  }

  const enrichLineItemFromModel = async (item) => {
    if (item.productLine && item.businessLine) return item

    const modelId = item.model
    if (!modelId) return item

    try {
      const res = await fetch(`${API_URL}models/${modelId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      const result = await res.json()
      const model = result.data || result.message
      if (!model) return item

      const hierarchy = getLineItemHierarchy(model)
      return {
        ...item,
        businessLine: hierarchy.businessLine || item.businessLine,
        productLine: hierarchy.productLine || item.productLine,
        productLineMeta: hierarchy.productLineMeta || item.productLineMeta,
        modelMeta: model
      }
    } catch {
      return item
    }
  }

  const prefetchLineItemLookups = async (items) => {
    const enrichedItems = await Promise.all(items.map(enrichLineItemFromModel))

    const businessLineGroups = new Map()
    enrichedItems.forEach(item => {
      if (!item.businessLine) return
      const extras = businessLineGroups.get(item.businessLine) || []
      if (item.productLineMeta) extras.push(item.productLineMeta)
      businessLineGroups.set(item.businessLine, extras)
    })

    await Promise.all(
      [...businessLineGroups.entries()].map(([bl, extras]) => loadProductLines(bl, extras))
    )

    await Promise.all(
      enrichedItems
        .filter(i => i.businessLine && i.productLine)
        .map(i => loadModels(i.businessLine, i.productLine, i.modelMeta ? [i.modelMeta] : []))
    )

    return enrichedItems
  }

  useEffect(() => {
    if (!isOpen) return
    const fetchLookups = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
        const [storesRes, vendorsRes, quotationsRes, businessLinesRes] = await Promise.all([
          fetch(`${API_URL}stores`, { headers }),
          fetch(`${API_URL}vendors?pagination=false`, { headers }),
          fetch(`${API_URL}quotations?pagination=false`, { headers }),
          fetch(`${API_URL}business-lines?pagination=false`, { headers })
        ])

        const storesJson = await storesRes.json()
        const vendorsJson = await vendorsRes.json()
        const quotationsJson = await quotationsRes.json()
        const businessLinesJson = await businessLinesRes.json()

        setStores(storesJson.data?.docs || storesJson.docs || [])
        setVendors(vendorsJson.data?.docs || vendorsJson.docs || [])
        setQuotations(quotationsJson.data?.docs || quotationsJson.docs || [])
        setBusinessLineList(
          (businessLinesJson.data?.docs || []).map(business => ({
            value: String(business.id || business._id),
            label: business.name
          }))
        )
      } catch {
        toast.error('Failed to load lookup data')
      }
    }
    fetchLookups()
  }, [isOpen, token])

  useEffect(() => {
    if (!isOpen || !purchaseOrder) return

    setSelectedVendor(purchaseOrder.vendor?._id || purchaseOrder.vendor || '')
    setSelectedQuotation(purchaseOrder.quotation?._id || purchaseOrder.quotation || '')
    setSelectedStore(purchaseOrder.store?._id || purchaseOrder.store || '')
    setDeliveryAddress(purchaseOrder.deliveryAddress || '')
    setCustomerAddress(purchaseOrder.customerAddress || '')
    setCustomerAddressPreset(detectCustomerPreset(purchaseOrder.customerAddress))
    setGsplGstin(purchaseOrder.gsplGstin || '')
    setOrderPlacedDate(toDateInput(purchaseOrder.orderPlacedDate || purchaseOrder.createdAt))
    setStatus(purchaseOrder.status || 'open')
    setTerms({
      priceValidity: purchaseOrder.priceValidity || '',
      paymentTerms: purchaseOrder.paymentTerms || '',
      packing: purchaseOrder.packing || '',
      freight: purchaseOrder.freight || '',
      transitInsurance: purchaseOrder.transitInsurance || ''
    })
    const mappedLineItems = (purchaseOrder.lineItems || []).map(li => {
        const modelObj = typeof li.model === 'object' ? li.model : null
        const hierarchy = getLineItemHierarchy(modelObj)
        return {
          localId: li._id,
          _id: li._id,
          businessLine: hierarchy.businessLine,
          productLine: hierarchy.productLine,
          productLineMeta: hierarchy.productLineMeta,
          model: modelObj?._id || li.model || '',
          modelMeta: modelObj,
          quantity: li.quantity ?? 0,
          perUnitPurchasePrice: li.perUnitPurchasePrice ?? '',
          totalPurchasePrice: li.totalPurchasePrice ?? '',
          gst: li.gst ?? 18,
          requiredDateOfMaterial: toDateInput(li.requiredDateOfMaterial),
          committedDeliveryDate: toDateInput(li.committedDeliveryDate)
        }
      })
    setLineItems(mappedLineItems)
    productLinesCacheRef.current = {}
    modelsCacheRef.current = {}
    setProductLinesCache({})
    setModelsCache({})
    setErrors({})

    prefetchLineItemLookups(mappedLineItems).then(enrichedItems => {
      setLineItems(enrichedItems)
    })
  }, [isOpen, purchaseOrder])

  if (!isOpen || !purchaseOrder) return null

  const getProductLineOptions = (item) => {
    if (!item.businessLine) return []
    const cached = productLinesCache[item.businessLine] || []
    if (item.productLine && item.productLineMeta) {
      const plOption = toProductLineOption(item.productLineMeta)
      if (!cached.some(o => o.value === plOption.value)) {
        return [plOption, ...cached]
      }
    }
    return cached
  }

  const getProductLineValue = (item, options) => {
    if (!item.productLine) return null
    const match = options.find(o => o.value === String(item.productLine))
    if (match) return match
    if (item.productLineMeta) return toProductLineOption(item.productLineMeta)
    return { value: String(item.productLine), label: 'Product Line' }
  }

  const getModelOptions = (item) => {
    if (!item.businessLine || !item.productLine) return []
    const cacheKey = `${item.businessLine}:${item.productLine}`
    const cached = modelsCache[cacheKey] || []
    if (item.modelMeta && !cached.some(o => o.value === String(item.model))) {
      return [...cached, toModelOption(item.modelMeta)]
    }
    return cached
  }

  const handleBusinessLineChange = async (localId, option) => {
    const businessLine = option?.value || ''
    setLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? { ...li, businessLine, productLine: '', productLineMeta: null, model: '', modelMeta: null }
          : li
      )
    )
    if (businessLine) await loadProductLines(businessLine)
    if (errors[`line_${localId}_businessLine`]) {
      setErrors(p => ({ ...p, [`line_${localId}_businessLine`]: '' }))
    }
  }

  const handleProductLineChange = async (localId, businessLine, option) => {
    const productLine = option?.value || ''
    setLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? {
              ...li,
              productLine,
              productLineMeta: option?.productLineData || null,
              model: '',
              modelMeta: null
            }
          : li
      )
    )
    if (businessLine && productLine) {
      await loadModels(businessLine, productLine)
    }
    if (errors[`line_${localId}_productLine`]) {
      setErrors(p => ({ ...p, [`line_${localId}_productLine`]: '' }))
    }
  }

  const handleModelChange = (localId, option) => {
    setLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? {
              ...li,
              model: option?.value || '',
              modelMeta: option?.modelData || null
            }
          : li
      )
    )
    if (errors[`line_${localId}_model`]) {
      setErrors(p => ({ ...p, [`line_${localId}_model`]: '' }))
    }
  }

  const handleCustomerPresetChange = (e) => {
    const preset = e.target.value
    setCustomerAddressPreset(preset)
    setCustomerAddress(preset ? CUSTOMER_ADDRESSES[preset] : '')
    if (errors.customerAddress) setErrors(p => ({ ...p, customerAddress: '' }))
  }

  const handleStoreChange = (e) => {
    const storeId = e.target.value
    setSelectedStore(storeId)
    const found = stores.find(s => s._id === storeId)
    if (found?.location) setDeliveryAddress(found.location)
    if (errors.store) setErrors(p => ({ ...p, store: '' }))
  }

  const handleTermsChange = e => {
    const { name, value } = e.target
    setTerms(prev => ({ ...prev, [name]: value }))
  }

  const updateLineItem = (localId, field, value) => {
    setLineItems(prev =>
      prev.map(li => {
        if (li.localId !== localId) return li
        const updated = { ...li, [field]: value }
        if (field === 'perUnitPurchasePrice') {
          const qty = Number(updated.quantity) || 0
          updated.totalPurchasePrice =
            value !== '' && qty ? (Number(value) * qty).toFixed(2) : ''
        }
        if (field === 'totalPurchasePrice') {
          const qty = Number(updated.quantity) || 0
          updated.perUnitPurchasePrice =
            value !== '' && qty ? (Number(value) / qty).toFixed(2) : ''
        }
        if (field === 'quantity') {
          const perUnit = Number(updated.perUnitPurchasePrice)
          if (perUnit && value) {
            updated.totalPurchasePrice = (perUnit * Number(value)).toFixed(2)
          }
        }
        return updated
      })
    )
  }

  const addLineItem = () => {
    setLineItems(prev => [...prev, newLineItem()])
  }

  const removeLineItem = (localId) => {
    setLineItems(prev => prev.filter(li => li.localId !== localId))
  }

  const { totalTaxableValue, totalTaxValue, totalValue } = (() => {
    let taxable = 0
    let tax = 0
    lineItems.forEach(item => {
      const perUnit = Number(item.perUnitPurchasePrice) || 0
      const qty = Number(item.quantity) || 0
      const gstRate = Number(item.gst) || 0
      const assessable = perUnit * qty
      taxable += assessable
      tax += (assessable * gstRate) / 100
    })
    return {
      totalTaxableValue: Math.round(taxable * 100) / 100,
      totalTaxValue: Math.round(tax * 100) / 100,
      totalValue: Math.round((taxable + tax) * 100) / 100
    }
  })()

  const validate = () => {
    const e = {}
    if (!selectedVendor) e.vendor = 'Please select a vendor'
    if (!customerAddress?.trim()) e.customerAddress = 'Please enter billing address'
    if (!selectedStore) e.store = 'Please select a store'
    if (!deliveryAddress?.trim()) e.deliveryAddress = 'Delivery address is required'
    if (!gsplGstin) e.gsplGstin = 'Please select GSPL GSTIN'
    if (!orderPlacedDate) e.orderPlacedDate = 'Order date is required'

    const requiredTerms = ['priceValidity', 'paymentTerms', 'packing', 'freight', 'transitInsurance']
    requiredTerms.forEach(f => {
      if (!terms[f]?.trim()) e[`term_${f}`] = 'Required'
    })

    if (lineItems.length === 0) {
      e.lineItems = 'At least one line item is required'
    }

    lineItems.forEach(li => {
      if (!li.businessLine) e[`line_${li.localId}_businessLine`] = 'Select business line'
      if (!li.productLine) e[`line_${li.localId}_productLine`] = 'Select product line'
      if (!li.model) e[`line_${li.localId}_model`] = 'Select model'
      if (!Number(li.quantity) || Number(li.quantity) <= 0) {
        e[`line_${li.localId}_qty`] = 'Invalid qty'
      }
      if (li.perUnitPurchasePrice === '' && li.totalPurchasePrice === '') {
        e[`line_${li.localId}_price`] = 'Enter price'
      }
      if (!li.requiredDateOfMaterial) e[`line_${li.localId}_reqDate`] = 'Required'
      if (!li.committedDeliveryDate) e[`line_${li.localId}_commitDate`] = 'Required'
    })

    return e
  }

  const handleSubmit = async () => {
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      toast.warn('Please fix validation errors before saving.')
      return
    }

    const payload = {
      vendor: selectedVendor,
      quotation: selectedQuotation || null,
      store: selectedStore,
      customerAddress,
      deliveryAddress,
      gsplGstin,
      orderPlacedDate: new Date(orderPlacedDate).toISOString(),
      status,
      ...terms,
      lineItems: lineItems.map(li => {
        const qty = Number(li.quantity)
        const perUnit = Number(li.perUnitPurchasePrice) || 0
        const item = {
          model: li.model,
          quantity: qty,
          perUnitPurchasePrice: perUnit,
          totalPurchasePrice: Number(li.totalPurchasePrice) || perUnit * qty,
          gst: Number(li.gst) || 0,
          requiredDateOfMaterial: li.requiredDateOfMaterial,
          committedDeliveryDate: new Date(li.committedDeliveryDate).toISOString()
        }
        if (li._id) item._id = li._id
        return item
      })
    }

    setLoading(true)
    try {
      const res = await fetch(`${API_URL}purchase-orders/${purchaseOrder._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
      const result = await res.json()
      if (!res.ok || result.error) {
        throw new Error(result.message || 'Failed to update purchase order')
      }
      toast.success(result.message || 'Purchase Order updated successfully')
      onClose()
      if (onSuccess) onSuccess(result.data)
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const inputCls = (hasErr) =>
    `w-full px-4 py-2 bg-slate-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm text-slate-800 ${hasErr ? 'border-rose-400 bg-rose-50 focus:ring-rose-500' : 'border-slate-200'}`

  const textareaCls = (hasErr) =>
    `w-full px-4 py-2 bg-slate-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm text-slate-800 resize-none ${hasErr ? 'border-rose-400 bg-rose-50 focus:ring-rose-500' : 'border-slate-200'}`

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm bg-slate-900/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">

        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-0">
              <span className="bg-amber-100 text-amber-600 p-1.5 rounded-lg">
                <IndianRupee size={20} />
              </span>
              Edit Purchase Order
            </h2>
            <p className="text-xs text-slate-500 mt-1 font-medium mb-0">
              PO: <span className="font-semibold text-slate-700">{purchaseOrder.poNumber}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors border-0 bg-transparent">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6" style={{ maxHeight: 'calc(95vh - 140px)' }}>

          {/* Vendor, Quotation, Status, Order Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">
                Vendor <span className="text-rose-500">*</span>
              </label>
              <select
                className={inputCls(!!errors.vendor)}
                value={selectedVendor}
                onChange={e => {
                  setSelectedVendor(e.target.value)
                  if (errors.vendor) setErrors(p => ({ ...p, vendor: '' }))
                }}
              >
                <option value="">-- Select Vendor --</option>
                {vendors.map(v => (
                  <option key={v._id} value={v._id}>{v.vendorName}</option>
                ))}
              </select>
              {errors.vendor && <p className="text-xs text-rose-500 mt-1 mb-0">{errors.vendor}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">Quotation</label>
              <select
                className={inputCls(false)}
                value={selectedQuotation}
                onChange={e => setSelectedQuotation(e.target.value)}
              >
                <option value="">-- None --</option>
                {quotations.map(q => (
                  <option key={q._id} value={q._id}>
                    {q.quotationId || q._id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">Status</label>
              <select
                className={inputCls(false)}
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">
                Order Date <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                className={inputCls(!!errors.orderPlacedDate)}
                value={orderPlacedDate}
                onChange={e => {
                  setOrderPlacedDate(e.target.value)
                  if (errors.orderPlacedDate) setErrors(p => ({ ...p, orderPlacedDate: '' }))
                }}
              />
              {errors.orderPlacedDate && <p className="text-xs text-rose-500 mt-1 mb-0">{errors.orderPlacedDate}</p>}
            </div>
          </div>

          {/* GSPL GSTIN */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
            <label className="block text-sm font-bold text-slate-700 mb-2">
              GSPL GSTIN <span className="text-rose-500">*</span>
            </label>
            <select
              className={`${inputCls(!!errors.gsplGstin)} w-full md:w-1/2`}
              value={gsplGstin}
              onChange={e => {
                setGsplGstin(e.target.value)
                if (errors.gsplGstin) setErrors(p => ({ ...p, gsplGstin: '' }))
              }}
            >
              <option value="">-- Select GSPL GSTIN --</option>
              <option value="19AAECG1743K1ZG">19AAECG1743K1ZG</option>
              <option value="18AAECG1743K1ZI">18AAECG1743K1ZI</option>
            </select>
            {errors.gsplGstin && <p className="text-xs text-rose-500 mt-1 mb-0">{errors.gsplGstin}</p>}
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
              <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <MapPin size={15} className="text-blue-500" /> Billing Address
                <span className="text-rose-500">*</span>
              </label>
              <select
                className={`${inputCls(!!errors.customerAddress)} mb-3`}
                value={customerAddressPreset}
                onChange={handleCustomerPresetChange}
              >
                <option value="">-- Select Office Location --</option>
                <option value="SILIGURI">Siliguri</option>
                <option value="KOLKATA">Kolkata</option>
                <option value="GUWAHATI">Guwahati</option>
              </select>
              <textarea
                rows={5}
                className={textareaCls(!!errors.customerAddress)}
                value={customerAddress}
                onChange={e => {
                  setCustomerAddress(e.target.value)
                  if (errors.customerAddress) setErrors(p => ({ ...p, customerAddress: '' }))
                }}
              />
              {errors.customerAddress && <p className="text-xs text-rose-500 mt-1 mb-0">{errors.customerAddress}</p>}
            </div>

            <div className="bg-green-50 p-5 rounded-xl border border-green-100">
              <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Truck size={15} className="text-green-600" /> Shipping Address
                <span className="text-rose-500">*</span>
              </label>
              <select
                className={`${inputCls(!!errors.store)} mb-3`}
                value={selectedStore}
                onChange={handleStoreChange}
              >
                <option value="">-- Select Store --</option>
                {stores.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              {errors.store && <p className="text-xs text-rose-500 mb-2">{errors.store}</p>}
              <textarea
                rows={5}
                className={textareaCls(!!errors.deliveryAddress)}
                value={deliveryAddress}
                onChange={e => {
                  setDeliveryAddress(e.target.value)
                  if (errors.deliveryAddress) setErrors(p => ({ ...p, deliveryAddress: '' }))
                }}
              />
              {errors.deliveryAddress && <p className="text-xs text-rose-500 mt-1 mb-0">{errors.deliveryAddress}</p>}
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-0 flex items-center gap-2">
                <IndianRupee size={13} /> Line Items
              </h3>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus size={14} /> Add Line Item
              </button>
            </div>
            {errors.lineItems && <p className="text-xs text-rose-500 mb-2">{errors.lineItems}</p>}

            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left align-middle">
                  <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-widest">
                    <tr>
                      {['#', 'Business Line', 'Product Line', 'Model', 'Part Code', 'UOM', 'HSN', 'Qty', 'Per Unit (₹)', 'GST %', 'Total (₹)', 'Required Date', 'Committed Date', ''].map(h => (
                        <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {lineItems.map((item, idx) => {
                      const productLineOptions = getProductLineOptions(item)
                      const productLineValue = getProductLineValue(item, productLineOptions)
                      const modelOptions = getModelOptions(item)
                      const modelDetails = item.modelMeta

                      return (
                        <tr key={item.localId} className="hover:bg-slate-50 align-middle">
                          <td className="px-2 py-1.5 text-slate-400">{idx + 1}</td>
                          <td className="px-2 py-1.5 min-w-[130px]">
                            <Select
                              {...selectProps}
                              options={businessLineList}
                              placeholder="business line"
                              isClearable
                              hasError={!!errors[`line_${item.localId}_businessLine`]}
                              value={businessLineList.find(o => o.value === item.businessLine) || null}
                              onChange={option => handleBusinessLineChange(item.localId, option)}
                            />
                          </td>
                          <td className="px-2 py-1.5 min-w-[130px]">
                            <Select
                              {...selectProps}
                              options={productLineOptions}
                              placeholder="product line"
                              isClearable
                              isDisabled={!item.businessLine}
                              hasError={!!errors[`line_${item.localId}_productLine`]}
                              value={productLineValue}
                              onChange={option => handleProductLineChange(item.localId, item.businessLine, option)}
                            />
                          </td>
                          <td className="px-2 py-1.5 min-w-[160px]">
                            <Select
                              {...selectProps}
                              options={modelOptions}
                              placeholder="model"
                              isClearable
                              isDisabled={!item.productLine}
                              hasError={!!errors[`line_${item.localId}_model`]}
                              value={modelOptions.find(o => o.value === String(item.model)) || null}
                              onChange={option => handleModelChange(item.localId, option)}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono text-slate-600 whitespace-nowrap">
                            {modelDetails?.partCode || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">
                            {modelDetails?.uom || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono text-slate-600 whitespace-nowrap">
                            {modelDetails?.HSNNumber || '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={e => updateLineItem(item.localId, 'quantity', e.target.value)}
                              className={`w-20 px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_qty`] ? 'border-rose-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.perUnitPurchasePrice}
                              onChange={e => updateLineItem(item.localId, 'perUnitPurchasePrice', e.target.value)}
                              className={`w-28 px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_price`] ? 'border-rose-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                              value={item.gst}
                              onChange={e => updateLineItem(item.localId, 'gst', e.target.value)}
                            >
                              <option value={0.1}>0.1</option>
                              <option value={5}>5</option>
                              <option value={12}>12</option>
                              <option value={18}>18</option>
                              <option value={28}>28</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.totalPurchasePrice}
                              onChange={e => updateLineItem(item.localId, 'totalPurchasePrice', e.target.value)}
                              className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={item.requiredDateOfMaterial}
                              onChange={e => updateLineItem(item.localId, 'requiredDateOfMaterial', e.target.value)}
                              className={`px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_reqDate`] ? 'border-rose-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={item.committedDeliveryDate}
                              onChange={e => updateLineItem(item.localId, 'committedDeliveryDate', e.target.value)}
                              className={`px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_commitDate`] ? 'border-rose-400' : 'border-gray-300'}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => removeLineItem(item.localId)}
                              disabled={lineItems.length <= 1}
                              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg border-0 bg-transparent disabled:opacity-30"
                              title="Remove line item"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-slate-800 border-t-2 border-slate-700 px-6 py-4">
                <div className="flex flex-wrap items-center justify-end gap-6">
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Taxable Value</p>
                    <p className="text-base font-bold text-slate-200 mb-0">₹ {totalTaxableValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Total Tax (GST)</p>
                    <p className="text-base font-bold text-amber-400 mb-0">₹ {totalTaxValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right border-l border-slate-600 pl-6">
                    <p className="text-[10px] font-bold text-green-400 uppercase tracking-widest mb-0.5">Grand Total</p>
                    <p className="text-2xl font-extrabold text-green-400 mb-0">₹ {totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="bg-white p-5 rounded-xl border border-slate-100">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <ShieldCheck size={14} /> Terms & Conditions
            </h3>
            <Row>
              {[
                { label: 'Price Basis', name: 'priceValidity', placeholder: 'e.g. 30 Days' },
                { label: 'Payment Terms', name: 'paymentTerms', placeholder: 'e.g. 50% Advance' },
                { label: 'Packing', name: 'packing', placeholder: 'e.g. Standard Packing' },
                { label: 'Freight', name: 'freight', placeholder: 'e.g. Extra at Actuals' },
                { label: 'Transit Insurance', name: 'transitInsurance', placeholder: 'e.g. By Vendor' }
              ].map(f => (
                <Col md="4" key={f.name} className="mb-3">
                  <FormGroup>
                    <Label className="text-xs font-bold text-gray-600 mb-1">
                      {f.label} <span className="text-rose-400">*</span>
                    </Label>
                    <Input
                      name={f.name}
                      value={terms[f.name]}
                      onChange={handleTermsChange}
                      placeholder={f.placeholder}
                      className={`text-sm ${errors[`term_${f.name}`] ? 'border-rose-400' : 'border-gray-300'}`}
                    />
                    {errors[`term_${f.name}`] && (
                      <small className="text-rose-500">{errors[`term_${f.name}`]}</small>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2.5 text-sm font-bold text-white bg-amber-600 border-0 rounded-lg hover:bg-amber-700 transition-all flex items-center gap-2 shadow-sm"
          >
            {loading ? (
              <><Spinner size="sm" className="text-white" /> Saving...</>
            ) : (
              <><IndianRupee size={15} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditPurchaseOrder
